import AccountSwitcher from '@ui/AccountSwitcher'
import BrandMark from '@ui/BrandMark'
import { IconBack } from '@ui/icons'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

type Props =
  | { variant: 'list' }
  | { variant: 'event'; title: string; rightSlot?: React.ReactNode }
  | { variant: 'simple'; title: string; rightSlot?: React.ReactNode }

// The back arrow navigates by URL parent, NOT `navigate(-1)`. History
// entries accumulate from tab switches, sheet toggles, query-param
// edits, and other in-page navigations — unwinding through them can
// take 10+ clicks to leave a detail screen. Navigating to a computed
// parent path always makes progress.
//
// Event detail (`/events/:id/*`) collapses back to `/events` in one
// step regardless of which sub-tab you're on. Everything else falls
// back to a single-segment strip, so `/settings/foo/bar` → `/settings/foo`
// → `/settings`. If we're already at `/`, stay put.
const SCORECARD_ORIGINS = new Set(['summary', 'players', 'flights', 'teams'])

const parentPath = (pathname: string, search: string): string => {
  // The scorecard is a leaf reached from a tab; back returns to that origin
  // (carried in ?from=), not the event list.
  const scorecard = pathname.match(
    /^\/(events\/[^/]+)\/players\/[^/]+\/scorecard/
  )
  if (scorecard) {
    const from = new URLSearchParams(search).get('from') ?? 'players'
    const tab = SCORECARD_ORIGINS.has(from) ? from : 'players'
    return `/${scorecard[1]}/${tab}`
  }
  const eventDetail = pathname.match(/^\/events\/[^/]+/)
  if (eventDetail) return '/events'
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length <= 1) return '/'
  return `/${segments.slice(0, -1).join('/')}`
}

const MobileTopbar = (props: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()

  return (
    <header
      className="sticky top-0 z-10 bg-green-900 text-white flex items-center gap-2 px-4 py-2.5 min-h-top-app"
      style={{ paddingTop: 'max(10px, env(safe-area-inset-top))' }}
    >
      {props.variant === 'list' ? (
        <>
          <BrandMark />
          <div className="flex-1 min-w-0">
            <AccountSwitcher variant="light" />
          </div>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => navigate(parentPath(pathname, search))}
            aria-label={t('common.back')}
            className="w-9 h-9 grid place-items-center rounded-lg bg-white/10"
          >
            <IconBack size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg tracking-tight leading-tight truncate">
              {props.title}
            </div>
          </div>
          {props.rightSlot}
        </>
      )}
    </header>
  )
}

export default MobileTopbar
